import React, { useCallback, useEffect, useState } from "react";
import { Download, Loader2, Mail, Printer } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import api, { apiError } from "@/lib/api";
import { useLang } from "@/i18n";
import { Panel, Empty } from "@/pages/shared";
import Stars from "@/components/Stars";

const ASPECTS = [["cleanliness", "gb_cleanliness"], ["sanitary", "gb_sanitary"], ["welcome", "gb_welcome"], ["value", "gb_value"]];
const fmt = (d) => (d ? new Date(d).toLocaleDateString("nl-BE", { day: "2-digit", month: "2-digit", year: "numeric" }) : "");

const Kpi = ({ label, value, stars }) => (
  <div className="rounded-xl border border-border bg-muted/30 px-4 py-3">
    <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">{label}</p>
    {stars ? (
      <div className="mt-1.5 flex items-center gap-2">
        <Stars value={value || 0} size="sm" />
        <span className="text-sm font-semibold tabular-nums">{value ? value.toFixed(2) : "—"}</span>
      </div>
    ) : (
      <p className="mt-1 text-2xl font-semibold tabular-nums">{value}</p>
    )}
  </div>
);

export default function AdminReviews() {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setData((await api.get("/reviews")).data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const togglePublic = async (row, value) => {
    try {
      await api.patch(`/reviews/${row.id}`, { public: value });
      setData((d) => ({ ...d, rows: d.rows.map((r) => (r.id === row.id ? { ...r, public: value } : r)) }));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const invite = async (dryRun) => {
    setBusy(true);
    try {
      const { data: res } = await api.post(`/reviews/invite/run?dry_run=${dryRun}`);
      if (res.skipped) { toast.info(t("gb_enabled")); return; }
      toast.success(dryRun
        ? t("gb_candidates").replace("{n}", res.candidates)
        : t("gb_sent_count").replace("{n}", res.sent));
      if (!dryRun) await load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const csv = () => {
    if (!data) return;
    const head = [t("gb_guest"), t("gb_stay"), t("gb_overall"), ...ASPECTS.map(([, k]) => t(k)), t("gb_comment"), t("gb_public")];
    const lines = data.rows.map((r) => [
      r.guest_name, `${r.pitch} ${fmt(r.arrival)}-${fmt(r.departure)}`, r.overall ?? "",
      ...ASPECTS.map(([k]) => (r.scores || {})[k] ?? ""),
      (r.comment || "").replace(/[\r\n;]+/g, " "), r.public ? "ja" : "nee",
    ]);
    const blob = new Blob([[head, ...lines].map((l) => l.join(";")).join("\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "gastenboek.csv";
    a.click();
  };

  const avg = data?.averages || {};

  return (
    <Panel
      title={t("admin_guestbook")}
      testid="guestbook-panel"
      action={
        <div className="flex flex-wrap items-center gap-2 print:hidden">
          <Button variant="outline" size="sm" onClick={() => invite(true)} disabled={busy} data-testid="gb-dry-run">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("gb_dry_run")}
          </Button>
          <Button variant="outline" size="sm" onClick={() => invite(false)} disabled={busy} data-testid="gb-send-now">
            <Mail className="h-4 w-4 mr-1" />{t("gb_send_now")}
          </Button>
          <Button variant="outline" size="sm" onClick={csv} data-testid="gb-csv"><Download className="h-4 w-4 mr-1" />CSV</Button>
          <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="gb-print"><Printer className="h-4 w-4 mr-1" />{t("print")}</Button>
        </div>
      }
    >
      <div className="p-5 space-y-5">
        <p className="text-sm text-muted-foreground">{t("admin_guestbook_desc")}</p>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="gb-kpis">
          <Kpi label={t("gb_invited")} value={data?.invited ?? 0} />
          <Kpi label={t("gb_submitted")} value={data?.submitted ?? 0} />
          <Kpi label={t("gb_rate")} value={`${data?.response_rate ?? 0}%`} />
          <Kpi label={t("gb_overall")} value={avg.overall} stars />
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {ASPECTS.map(([key, label]) => <Kpi key={key} label={t(label)} value={avg[key]} stars />)}
        </div>

        <p className="text-xs text-muted-foreground">{t("gb_public_hint")}</p>

        {!data || data.rows.length === 0 ? <Empty>{t("gb_none")}</Empty> : (
          <div className="overflow-x-auto">
            <Table data-testid="gb-table">
              <TableHeader>
                <TableRow>
                  <TableHead>{t("gb_guest")}</TableHead>
                  <TableHead>{t("gb_stay")}</TableHead>
                  <TableHead>{t("gb_overall")}</TableHead>
                  <TableHead className="min-w-[16rem]">{t("gb_comment")}</TableHead>
                  <TableHead className="text-center">{t("gb_public")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.rows.map((r) => (
                  <TableRow key={r.id} data-testid={`gb-row-${r.id}`}>
                    <TableCell className="font-medium whitespace-nowrap">
                      {r.guest_name}
                      <span className="block text-[11px] uppercase text-muted-foreground">{r.language}</span>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm">
                      <span className="font-mono">{r.pitch}</span>
                      <span className="block text-[11px] text-muted-foreground tabular-nums">{fmt(r.arrival)} → {fmt(r.departure)}</span>
                    </TableCell>
                    <TableCell>
                      {r.submitted_at ? (
                        <div className="flex items-center gap-2">
                          <Stars value={r.overall} size="sm" />
                          <span className="text-xs tabular-nums text-muted-foreground">{r.overall}/5</span>
                        </div>
                      ) : <span className="text-xs text-muted-foreground">{t("gb_waiting")}</span>}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground align-top py-3">{r.comment || "—"}</TableCell>
                    <TableCell className="text-center">
                      <Checkbox checked={!!r.public} disabled={!r.submitted_at} data-testid={`gb-public-${r.id}`}
                        onCheckedChange={(v) => togglePublic(r, !!v)} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </Panel>
  );
}
